import { Fork, Invoke } from '@swf/components/StarsWizard/Flow';
import useWizardExit from '@swf/core/hooks/useWizardExit';
import type { FlowConfig } from '@swf/core/types/Wizard';
import { Action } from '@swf/core/types/Wizard';

import RMFAddress from '../components/RMFAddress/RMFAddress';
import RMFDOB from '../components/RMFDOB/RMFDOB';
import RMFDepositLimit from '../components/RMFDepositLimit/RMFDepositLimit';
import RMFGender from '../components/RMFGender/RMFGender';
import RMFKBA from '../components/RMFKBA/RMFKBA';
import RMFLegalName from '../components/RMFLegalName/RMFLegalName';
import RMFPhone from '../components/RMFPhone/RMFPhone';
import RMFPhoneVerification from '../components/RMFPhoneVerification/RMFPhoneVerification';
import RMFSSN from '../components/RMFSSN/RMFSSN';
import RMFSSNConfirmation from '../components/RMFSSN/components/RMFSSNConfirmation/RMFSSNConfirmation';
import RMFSecurityQuestions from '../components/RMFSecurityQuestions/RMFSecurityQuestions';
import RMFStandaloneFinalPage from '../components/RMFStandaloneFinalPage/RMFStandaloneFinalPage';
import RMFTermsOfService from '../components/RMFTermsOfService/RMFTermsOfService';

import useRMFBaseSteps from './useRMFBaseSteps';
import { Guard } from './useRmfGuards';
import { Service } from './useRmfServices';

const useRmfUSMIFlow = (): FlowConfig => {
  const exitWizard = useWizardExit();

  const invokeRmfRealMoneyInformationCheck = new Invoke(
    Service.RmfRealMoneyInformationCheck,
    RMFPhone
  );

  const invokeRmfDobUpdate = new Invoke(
    Service.RmfDobUpdate,
    invokeRmfRealMoneyInformationCheck,
    RMFDOB
  );

  const forkDobCheck = new Fork(
    Guard.RmfDobEnabled,
    [RMFDOB, invokeRmfDobUpdate],
    [invokeRmfRealMoneyInformationCheck]
  );
  const invokeSignupDob = new Invoke(Service.SignupDobFetch, forkDobCheck);

  const jumpToFeatureStep = {
    name: 'jumpToFeatureStep',
    entry: Action.GoJump
  };

  const { forkSignupDepositCheckDone } = useRMFBaseSteps();

  const forkJumpToFeature = new Fork(
    Guard.DoesJumpToFeatureStep,
    [new Invoke(Service.SetJumpCreator, jumpToFeatureStep)],
    [forkSignupDepositCheckDone]
  );

  const forkRmfExit = new Fork(
    Guard.StandaloneRmf,
    [
      RMFStandaloneFinalPage,
      {
        name: 'exitRMF',
        entry: exitWizard,
        type: 'final'
      }
    ],
    [forkJumpToFeature]
  );

  const forkKBA = new Fork(
    Guard.WrmfKBA,
    [RMFKBA, forkRmfExit, jumpToFeatureStep],
    [forkRmfExit, jumpToFeatureStep]
  );

  const invokeSSNSave = new Invoke(Service.RmfSSNSave, forkKBA, RMFDepositLimit);

  const invokeDepositLimitSave = new Invoke(
    Service.RmfDepositLimitSave,
    invokeSSNSave,
    RMFDepositLimit
  );
  const invokeLegalNameSave = new Invoke(Service.RmfLegalNameSave, RMFSSN, RMFLegalName);
  const invokeGenderSave = new Invoke(Service.RmfGenderSave, RMFAddress, RMFGender);

  const invokeSecurityQuestionsSave = new Invoke(
    Service.RmfSecurityQuestionsSave,
    RMFDepositLimit,
    RMFSecurityQuestions
  );

  const forkChallengeQuestionsCheck = new Fork(
    Guard.RmfChallengeQuestionsCheck,
    [RMFSecurityQuestions, invokeSecurityQuestionsSave, RMFDepositLimit],
    [RMFDepositLimit]
  );

  const invokeAddressSave = new Invoke(
    Service.RmfAddressSave,
    forkChallengeQuestionsCheck,
    RMFAddress
  );

  const forkDepositLimits = new Fork(
    Guard.LimitsSelected,
    [invokeSSNSave],
    [invokeDepositLimitSave, invokeSSNSave]
  );

  const invokeMobileCodeVerification = new Invoke(
    Service.RmfMobilePhoneVerificationSave,
    RMFLegalName,
    RMFPhoneVerification
  );

  const forkPhoneVerificationCheck = new Fork(
    Guard.PhoneVerificationCheck,
    [RMFPhoneVerification, invokeMobileCodeVerification, RMFLegalName],
    [RMFLegalName]
  );

  const invokeMobilePhoneSave = new Invoke(
    Service.RmfMobilePhoneSave,
    forkPhoneVerificationCheck,
    RMFPhone
  );

  const forkSSNValidationCheck = new Fork(
    Guard.IsSSNConfirmationRequired,
    [RMFSSNConfirmation, RMFGender],
    [RMFGender]
  );

  const invokeSSNConfirm = new Invoke(Service.RmfSSNConfirm, forkSSNValidationCheck, RMFSSN);

  return [
    invokeSignupDob,
    forkDobCheck,
    RMFPhone,
    invokeMobilePhoneSave,
    forkPhoneVerificationCheck,
    invokeLegalNameSave,
    RMFSSN,
    invokeSSNConfirm,
    forkSSNValidationCheck,
    invokeGenderSave,
    RMFAddress,
    invokeAddressSave,
    forkChallengeQuestionsCheck,
    RMFTermsOfService,
    forkDepositLimits,
    forkKBA
  ];
};

export default useRmfUSMIFlow;
